'use client';

import { useCallback, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';

import { useTheme } from '@/contexts/ThemeContext';
import { useAuth, useToggle } from '@/hooks';
import { useLogout } from '@/services/query';
import { useAppDispatch } from '@/store/hooks';
import { openModal } from '@/store/slices/ui.slice';

import { ROUTES } from '@/constants';

export const useUserSettingsMenu = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { mode: themeMode, toggleTheme } = useTheme();
  const { isAuthenticated, user } = useAuth();
  console.log('user: ', { user });
  const logoutMutation = useLogout();
  const anchorRef = useRef<HTMLDivElement>(null);

  const [isMenuVisible, toggleMenuVisibility, setMenuVisible] = useToggle(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

  const closeMenu = useCallback(() => setMenuVisible(false), [setMenuVisible]);

  const getMenuPosition = useCallback(() => {
    if (!anchorRef.current) return { top: 0, right: 0 };
    const rect = anchorRef.current.getBoundingClientRect();
    const rightOffset = window.innerWidth - rect.right;
    return {
      top: rect.bottom + 8,
      right: rightOffset > 16 ? rightOffset : 16,
    };
  }, []);

  const handleMenuToggle = useCallback(() => {
    if (!isMenuVisible) setMenuPosition(getMenuPosition());
    toggleMenuVisibility();
  }, [isMenuVisible, toggleMenuVisibility, getMenuPosition]);

  const handleLogout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
      closeMenu();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [logoutMutation, closeMenu]);

  const handleLinkClick = useCallback(
    (path?: string) => {
      if (path === ROUTES.REFERRALS && !user?.isReferredUser) {
        dispatch(openModal('referral'));
      } else if (path) {
        router.push(path);
      }
      closeMenu();
    },
    [router, closeMenu, user?.isReferredUser, dispatch]
  );

  return {
    anchorRef,
    isMenuVisible,
    menuPosition,
    handleMenuToggle,
    closeMenu,
    themeMode,
    toggleTheme,
    isAuthenticated,
    user,
    handleLogout,
    handleLinkClick,
    isLoggingOut: logoutMutation.isPending,
  };
};
